import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

function getDirectionFromLocale(localeName) {
  try {
    const locale = new Intl.Locale(localeName);
    return locale?.getTextInfo?.()?.direction ?? 'ltr'; // Returns "ltr" or "rtl"
  } catch (error) {
    console.error("Invalid locale code:", error);
    return 'ltr'; // Or a default value
  }
}

const isHex = str => (/^[0-9A-F]+$/i).test(str)



/**
 * An asbtraction of an icon to isolate use of FontAwesome
 * @component
 */
const Icon = ({ intent, literal, variant, kind, style }) => {
    const { i18n } = useTranslation();
    const intentMapping = useMemo(() => {
        const direction = getDirectionFromLocale(i18n.resolvedLanguage);
        return {
            expand: { variant: 'solid', kind: 'chevron-down', style: { transform: `rotate(${direction === 'ltr' ? '-' : ''}90deg)`, fontSize: '0.8em', transition: 'transform 180ms ease' } },
            collapse: { variant: 'solid', kind: direction === 'ltr' ? 'chevron-right' : 'chevron-left', style: { transform: `rotate(${direction === 'ltr' ? '' : '-'}90deg)`, fontSize: '0.8em', transition: 'transform 180ms ease' } },
            view: 'eye',
            edit: 'pencil',
            close: 'close',
            save: 'save',
            load: { variant: 'regular', kind: 'folder-open' },
            delete: 'trash-can',
            print: 'print',
            copy: 'copy',
            search: 'search',
            marker: 'location-dot',
            filter: 'filter',
            add: 'plus',
            sortDown: 'sort-down',
            sortUp: 'sort-up',
            sort: 'sort',
            move: 'up-down-left-right',
            download: 'download',
            upload: 'upload',
            left: 'chevron-left',
            right: 'chevron-right',
            up: 'chevron-up',
            down: 'chevron-down',
            clear: 'arrows-rotate',
            info: 'info-circle',
            locked: 'lock',
            unlocked: 'unlock',
            settings: 'gears',
            group: 'object-group',
            ungroup: 'object-ungroup',
            send: { variant: 'regular', kind: 'paper-plane' },
            preview: 'file-invoice',
            check: 'check',
            previous: 'chevron-left',
            next: 'chevron-right',
            repeat: 'rotate-right',
            ok: 'check',
            submit: 'check',
            cancel: 'xmark',
            apply: 'check',
            attachment: 'paperclip',
            select: { variant: 'regular', kind: 'hand-pointer' },
            guide: 'circle-question',
            back: 'chevron-left',
            popout: 'arrow-up-right-from-square',
            pin: 'thumbtack',
            unpin: 'thumbtack-slash',
            clock: 'clock', 
        }
    }, [i18n.resolvedLanguage]);

    if (intent) {
        if (intentMapping[intent] === undefined) {
            throw new Error(`Unknown icon intent ${intent}`);
        }
        const info = intentMapping[intent];
        if (typeof info === 'string') {
            variant = 'solid';
            kind = info;
        } else {
            variant = info.variant ?? 'solid';
            kind = info.kind;
            style = { ...info.style, ...style }
        }
    }

    if ((!literal && (!variant && !kind))
        || (variant && !kind) || (kind && !variant)) {
        return <i></i>;
    }

    if (literal) {
        if (isHex(literal)) {
            return <i className="fas" style={style}>{String.fromCodePoint(parseInt(literal, 16))}</i>
        } else {
            return <i className={literal} style={style}></i>
        }
    }
    if (!kind.startsWith('fa-')) {
        kind = 'fa-' + kind;
    }

    return <span style={style}><i className={`fa-${variant} ${kind}`}></i></span>
}

Icon.propTypes = {
    intent: PropTypes.string,
    literal: PropTypes.string,
    variant: PropTypes.oneOf(['solid', 'regular', 'brands']),
    kind: PropTypes.string,
    style: PropTypes.object
}

export default Icon;